import { compact } from 'lodash-es';
import { defineMessages, IntlShape, MessageDescriptor } from 'react-intl';
import * as yup from 'yup';

import { MY_COUNTRY_LOCATION } from '../../../common/constants';
import {
  BusinessVerificationLocationType,
  VerificationMethod,
} from '../../../common/interfaces/entities';
import {
  INDONESIA_COUNTRY,
  SINGAPORE_COUNTRY,
} from '../../../modules/JobV2/common/constants';
import { VerifyWithProofPersonalDocumentType } from './types';

const messages = defineMessages({
  nibLengthInvalid: {
    id: 'text-nib-length-is-13',
    defaultMessage: 'NIB number is invalid, it should be 13 digits.',
  },
  nibInvalid: {
    id: 'text-nib-number-invalid',
    defaultMessage: 'Invalid NIB number.',
  },
  npwpInvalid: {
    id: 'text-npwp-number-invalid',
    defaultMessage: 'Invalid NPWP number.',
  },
  ktpInvalid: {
    id: 'text-invalid-ktp-number',
    defaultMessage: 'Invalid KTP number.',
  },
  ktpDocumentRequired: {
    id: 'text-ktp-document-required',
    defaultMessage: 'KTP document required.',
  },
  ktpNumberRequired: {
    id: 'text-ktp-number-required',
    defaultMessage: 'KTP number is required.',
  },
  npwpDocumentRequired: {
    id: 'text-npwp-doc-required',
    defaultMessage: 'NPWP document required.',
  },
  npwpNumberRequired: {
    id: 'text-npwp-number-required',
    defaultMessage: 'NPWP number is required.',
  },
  businessImagesRequired: {
    id: 'text-min-business-images-required',
    defaultMessage: 'Min {count} business images required.',
  },
  companyUENInvalid: {
    id: 'text-error-invalid-uen',
    defaultMessage: 'Invalid UEN number.',
  },
  companyUENRequired: {
    id: 'text-error-uen-required',
    defaultMessage: 'UEN number required.',
  },
  acraBizFileRequired: {
    id: 'text-error-bizfile-required',
    defaultMessage: 'ACRA Bizfile required.',
  },
  companyNameRequired: {
    id: 'text-company-name-is-required',
    defaultMessage: 'Company name is required.',
  },
  legalNumberRequiredMy: {
    id: 'text-error-legal-number-required-my',
    defaultMessage: 'Business Registration Number (BRN) is required.',
  },
  legalNumberInvalidMy: {
    id: 'text-error-legal-number-invalid-my',
    defaultMessage: 'Invalid Business Registration Number (BRN).',
  },
  legalDocRequiredMy: {
    id: 'text-error-legal-doc-required-my',
    defaultMessage: 'Suruhanjaya Syarikat Malaysia (SSM) is required.',
  },
  legalNumberRequiredDefault: {
    id: 'text-error-legal-number-required-default',
    defaultMessage: 'Company Legal Number is required.',
  },
  legalNumberInvalidDefault: {
    id: 'text-error-legal-number-invalid-default',
    defaultMessage: 'Invalid Company Legal Number.',
  },
  legalDocRequiredDefault: {
    id: 'text-error-legal-doc-required-default',
    defaultMessage: 'Company Legal Document is required.',
  },
});

const MIN_BUSINESS_IMAGES_REQUIRED = 1;

const nibNumberValidation = (intl: IntlShape) =>
  yup
    .string()
    .trim()
    .optional()
    .test('isValidNib', intl.formatMessage(messages.nibInvalid), value => {
      if (!value) return true;
      return /^\d+$/.test(value);
    })
    .test(
      'hasCorrectLength',
      intl.formatMessage(messages.nibLengthInvalid),
      value => {
        if (!value) return true;
        return value.length === 13;
      }
    );

const npwpOrKTPNumberValidation = (
  intl: IntlShape,
  errorMessage: MessageDescriptor
) => {
  const formattedErrorMessage = intl.formatMessage(errorMessage);
  return yup
    .string()
    .optional()
    .matches(/^\d+$/, formattedErrorMessage)
    .min(15, formattedErrorMessage)
    .max(16, formattedErrorMessage);
};

const getLegalDocumentValidation = (
  intl: IntlShape,
  companyCountryCode?: string
) => {
  switch (companyCountryCode) {
    case SINGAPORE_COUNTRY.code:
      return yup.object().shape({
        acraBizFile: yup
          .string()
          .nullable()
          .transform(value => value ?? '')
          .trim()
          .required(intl.formatMessage(messages.acraBizFileRequired)),
        companyUniqueEntityNumber: yup
          .string()
          .nullable()
          .transform(value => value ?? '')
          .trim()
          .required(intl.formatMessage(messages.companyUENRequired))
          .matches(
            /^([0-9]{8}[A-Z]|[0-9]{9}[A-Z]|[STFG]\d{2}[A-Z0-9]{2}\d{4}[A-Z])$/,
            intl.formatMessage(messages.companyUENInvalid)
          ),
      });
    case INDONESIA_COUNTRY.code:
      return yup.object().shape({
        companyNPWPDoc: yup
          .string()
          .trim()
          .required(intl.formatMessage(messages.npwpDocumentRequired)),
        companyNPWPNumber: yup.string().when('legalDocumentNotOfficial', {
          is: true,
          then: () =>
            npwpOrKTPNumberValidation(intl, messages.npwpInvalid).required(
              intl.formatMessage(messages.npwpNumberRequired)
            ),
          otherwise: () => yup.string().optional().nullable(),
        }),
        companyNIBNumber: nibNumberValidation(intl),
        additionalDocuments: yup.array().optional(),
        // Only editable when company name doesn't match with NPWP document. Otherwise it's prefilled with the current name.
        companyName: yup.string().when('allowCompanyNameEdit', {
          is: true,
          then: yup
            .string()
            .required(intl.formatMessage(messages.companyNameRequired)),
          otherwise: yup.string().optional(),
        }),
      });
    case MY_COUNTRY_LOCATION.code:
      return yup.object().shape({
        companyLegalDoc: yup
          .string()
          .nullable()
          .transform(value => value ?? '')
          .trim()
          .required(intl.formatMessage(messages.legalDocRequiredMy)),
        companyLegalNumber: yup
          .string()
          .nullable()
          .transform(value => value ?? '')
          .trim()
          .required(intl.formatMessage(messages.legalNumberRequiredMy))
          .matches(
            /^(?:\d{12}|\d{5,8}-[A-Za-z])$/,
            intl.formatMessage(messages.legalNumberInvalidMy)
          ),
      });
    default:
      return yup.object().shape({
        companyLegalDoc: yup
          .string()
          .nullable()
          .transform(value => value ?? '')
          .trim()
          .required(intl.formatMessage(messages.legalDocRequiredDefault)),
        companyLegalNumber: yup
          .string()
          .nullable()
          .transform(value => value ?? '')
          .trim()
          .required(intl.formatMessage(messages.legalNumberRequiredDefault))
          .max(80, intl.formatMessage(messages.legalNumberInvalidDefault)),
      });
  }
};

export const companyVerificationFormValidator = (
  intl: IntlShape,
  companyCountryCode?: string
) =>
  yup.object().shape({
    verificationMethod: yup
      .string()
      .oneOf(Object.values(VerificationMethod))
      .required(),
    isViewOnly: yup.boolean().optional(),
    verifyWithLegalDocuments: yup.object().when('verificationMethod', {
      is: VerificationMethod.LEGAL_DOCUMENTS,
      then: getLegalDocumentValidation(intl, companyCountryCode),
    }),
    verifyWithProofOfOwnership: yup.object().when('verificationMethod', {
      is: VerificationMethod.PROOF_OF_OWNERSHIP,
      then: yup.object().shape({
        isWhatsAppVerified: yup.boolean().oneOf([true]),
        personalDocumentType: yup
          .string()
          .oneOf(Object.values(VerifyWithProofPersonalDocumentType))
          .required(),
        businessLocationType: yup
          .string()
          .oneOf(Object.values(BusinessVerificationLocationType))
          .required(),
        businessActivityImages: yup
          .array()
          .required()
          .test(
            'businessActivityMin',
            intl.formatMessage(messages.businessImagesRequired, {
              count: MIN_BUSINESS_IMAGES_REQUIRED,
            }),
            values => compact(values).length >= MIN_BUSINESS_IMAGES_REQUIRED
          ),
        personalNPWPDoc: yup
          .string()
          .trim()
          .when('personalDocumentType', {
            is: VerifyWithProofPersonalDocumentType.NPWP,
            then: yup
              .string()
              .required(intl.formatMessage(messages.npwpDocumentRequired)),
          }),
        personalNPWPNumber: yup.string().when('personalDocumentType', {
          is: VerifyWithProofPersonalDocumentType.NPWP,
          then: npwpOrKTPNumberValidation(intl, messages.npwpInvalid).required(
            intl.formatMessage(messages.npwpNumberRequired)
          ),
        }),
        ktpDoc: yup
          .string()
          .trim()
          .when('personalDocumentType', {
            is: VerifyWithProofPersonalDocumentType.KTP,
            then: yup
              .string()
              .required(intl.formatMessage(messages.ktpDocumentRequired)),
          }),
        ktpNumber: yup.string().when('personalDocumentType', {
          is: VerifyWithProofPersonalDocumentType.KTP,
          then: npwpOrKTPNumberValidation(intl, messages.ktpInvalid).required(
            intl.formatMessage(messages.ktpNumberRequired)
          ),
        }),
        personalNIBNumber: nibNumberValidation(intl),
        additionalDocuments: yup.array().optional(),
      }),
    }),
  });
